import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useMatches, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authClient } from '@/api/auth';
import { useIsAdmin, useIsDirector, useIsManagement, useIsStaff } from '@/hooks/useAuth';
import { xeroApi } from '@/api/xero';
import { toolsApi } from '@/api/tools';
import type { EntraSecretData } from '@/api/tools';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { useAppConfig } from '@/hooks/useAppConfig';
import {
  Home, ClipboardList, Calendar, Warehouse, Menu, Settings, Search,
  AlertTriangle, Clock, type LucideIcon,
} from 'lucide-react';
import { openCommandPalette } from '@/components/CommandPalette';

/**
 * Mobile-only app shell. Renders:
 *   - top bar with page title + menu button
 *   - main content area (full width)
 *   - bottom tab bar with the most-used destinations
 *   - slide-out drawer for everything else
 *
 * The desktop AppShell is completely independent and unchanged.
 */
export function MobileShell() {
  const matches = useMatches();
  const title = (matches[matches.length - 1]?.handle as any)?.title ?? '';
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const isAdmin = useIsAdmin();
  const isDirector = useIsDirector();
  const isManagement = useIsManagement();
  const isStaff = useIsStaff();
  const config = useAppConfig();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [entraWarning, setEntraWarning] = useState<EntraSecretData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Same background side-effects as AppShell.
  useEffect(() => {
    xeroApi.autoSyncContacts().catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    toolsApi.getEntraSecret()
      .then((data) => {
        if (data.configured && (data.status === 'warning' || data.status === 'expired')) {
          setEntraWarning(data);
        }
      })
      .catch(() => {});
  }, [isAdmin]);

  // Close drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    await authClient.signOut();
    setUser(null);
    window.location.href = '/login';
  }

  // Admin-only, as in AppShell — Manage lands on the admin-only System page.
  const showBanner = isAdmin && entraWarning && !dismissed;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f7' }}>
      <ImpersonationBanner />
      {/* Top bar */}
      <header style={topBarStyle}>
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          style={iconBtnStyle}
        >
          <Menu size={22} strokeWidth={2} />
        </button>
        <span style={{ fontWeight: 600, fontSize: 15, flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <button
          onClick={openCommandPalette}
          aria-label="Search"
          style={iconBtnStyle}
        >
          <Search size={20} strokeWidth={2.2} />
        </button>
      </header>

      {/* Entra warning banner (directors only) */}
      {showBanner && (
        <div style={{
          padding: '8px 14px',
          background: entraWarning.status === 'expired' ? '#fef2f2' : '#fffbeb',
          borderBottom: `2px solid ${entraWarning.status === 'expired' ? '#fca5a5' : '#fde68a'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 12,
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
            {entraWarning.status === 'expired'
              ? <AlertTriangle size={14} style={{ color: '#c33', flexShrink: 0 }} />
              : <Clock size={14} style={{ color: '#92400e', flexShrink: 0 }} />}
            <span style={{ fontWeight: 600, color: entraWarning.status === 'expired' ? '#c33' : '#92400e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entraWarning.status === 'expired'
                ? `Entra secret expired ${Math.abs(entraWarning.daysUntilExpiry!)}d ago`
                : `Entra secret expires in ${entraWarning.daysUntilExpiry}d`}
            </span>
            <Link to="/admin/system" style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none', fontSize: 11 }}>
              Manage
            </Link>
          </div>
          <button
            onClick={() => setDismissed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Main scroll area */}
      <main style={{ flex: 1, padding: 12, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 80 }}>
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav style={tabBarStyle}>
        <TabLink to="/dashboard" label="Home" icon={Home} />
        <TabLink to="/jobs" label="Jobs" icon={ClipboardList} />
        <TabLink to="/calendar" label="Calendar" icon={Calendar} />
        <TabLink to="/warehouse" label="Warehouse" icon={Warehouse} />
        <button
          onClick={() => setDrawerOpen(true)}
          style={{ ...tabBtnStyle, color: drawerOpen ? 'var(--brand)' : '#888' }}
        >
          <Menu size={20} strokeWidth={2} />
          <span style={{ fontSize: 10, marginTop: 3, fontWeight: 500 }}>More</span>
        </button>
      </nav>

      {/* Slide-out drawer */}
      {drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900,
            }}
          />
          <div style={drawerStyle}>
            <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #2a2a3e' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{user?.role}</div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
              <DrawerLink to="/dashboard" label="Dashboard" />
              <DrawerLink to="/calendar" label="Calendar" />
              <DrawerLink to="/warehouse" label="Warehouse" />

              <div style={drawerGroup}>Jobs</div>
              {isManagement && <DrawerLink to="/projects" label="Projects" />}
              <DrawerLink to="/jobs" label="Jobs" />
              {isManagement && <DrawerLink to="/clients" label="Clients" />}

              <div style={drawerGroup}>Inventory</div>
              <DrawerLink to="/inventory" label="Equipment" />

              <div style={drawerGroup}>Catalogues</div>
              {isManagement && <DrawerLink to="/catalogue/crew" label="Crew" />}
              <DrawerLink to="/catalogue/labour" label="Labour" />
              <DrawerLink to="/catalogue/transport" label="Transport" />
              <DrawerLink to="/catalogue/other" label="Other" />
              <DrawerLink to="/catalogue/sales" label="Sales" />
              {!isStaff && <DrawerLink to="/catalogue/subhire" label="Sub-Hire" />}

              {/* Opens at management for Analytics (its sales tab is management+);
                  everything else inside stays director-only. Mirrors Sidebar.tsx. */}
              {isManagement && (
                <>
                  <div style={drawerGroup}>Admin</div>
                  {isDirector && <DrawerLink to="/admin/categories" label="Categories" />}
                  {isDirector && <DrawerLink to="/admin/data" label="Data" />}
                  <DrawerLink to="/admin/analytics" label="Analytics" />
                  {isAdmin && <DrawerLink to="/admin/users" label="Users" />}
                  {isAdmin && <DrawerLink to="/admin/system" label="System" />}
                  {isAdmin && <DrawerLink to="/admin/activity-log" label="Activity Log" />}
                </>
              )}
            </div>

            <div style={{ padding: 16, borderTop: '1px solid #2a2a3e' }}>
              <Link
                to="/settings/notifications"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px', color: '#bbb', textDecoration: 'none', fontSize: 13, marginBottom: 8,
                }}
              >
                <Settings size={14} /> Notification settings
              </Link>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8,
                  background: '#2a2a3e', color: '#fff', border: 'none',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Sign out
              </button>
              {config?.version && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#6b6b80', marginTop: 10, letterSpacing: 0.3 }}>
                  v{config.version}
                  {config.environment && config.environment !== 'production' && ` · ${config.environment}`}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TabLink({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...tabBtnStyle,
        color: isActive ? 'var(--brand)' : '#888',
      })}
    >
      {({ isActive }) => (
        <>
          <Icon size={20} strokeWidth={isActive ? 2.4 : 2} />
          <span style={{ fontSize: 10, marginTop: 3, fontWeight: isActive ? 600 : 500 }}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function DrawerLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'block',
        padding: '12px 20px',
        color: isActive ? '#fff' : '#bbb',
        background: isActive ? 'rgba(108,99,255,0.15)' : 'transparent',
        borderLeft: isActive ? '3px solid #6c63ff' : '3px solid transparent',
        textDecoration: 'none',
        fontSize: 14,
      })}
    >
      {label}
    </NavLink>
  );
}

const topBarStyle: React.CSSProperties = {
  height: 52,
  background: '#fff',
  borderBottom: '1px solid #e8e8e8',
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  flexShrink: 0,
  position: 'sticky',
  top: 0,
  zIndex: 50,
};

const iconBtnStyle: React.CSSProperties = {
  width: 40, height: 40,
  background: 'transparent', border: 'none',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#444',
};

const tabBarStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 0, left: 0, right: 0,
  height: 64,
  background: '#fff',
  borderTop: '1px solid #e8e8e8',
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'stretch',
  zIndex: 100,
  paddingBottom: 'env(safe-area-inset-bottom)',
};

const tabBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
  padding: '6px 0',
};

const drawerStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0, bottom: 0, left: 0,
  width: 280,
  maxWidth: '85%',
  background: '#1a1a2e',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '2px 0 20px rgba(0,0,0,0.3)',
};

const drawerGroup: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  color: '#555',
  padding: '16px 20px 4px',
  textTransform: 'uppercase',
};
